import { IRulerInfo } from '@/editor/entities/2d/rulerModel';
import { globalStores } from '../..';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { EFaceSide, IWallEdge } from '@/editor/types';
import { getArcWallRulerInfos } from '@/editor/utils/ruler/wall';
import { MAX_CHORD_LENGTH, MAX_SAGITTA } from '@/editor/constant/wall';
import { WallData } from '@/editor/data/wall/Wall';

interface IWallRulerInfo extends IRulerInfo {
  wallId: string;
  startMovable?: boolean; // 起始点可移动
  endMovable?: boolean; // 终点可移动
  arcMidMovable?: boolean; // 圆弧中点可移动
}

export function getRelativeWallRulerInfos(mainWallId: string): IWallRulerInfo[] {
  const wallData = globalStores.wallStore.getWallData(mainWallId);
  if (!wallData) return [];

  // 墙体关联信息
  const relativeWallInfos = globalStores.wallStore.getConnectedWallInfo(mainWallId);
  if (!relativeWallInfos) return [];

  const rulerInfos: IWallRulerInfo[] = [];

  const startPointConnected = [
    ...relativeWallInfos.mainStartConnectToPoint,
    ...relativeWallInfos.mainStartOnWallInternal
  ];
  const endPointConnected = [...relativeWallInfos.mainEndConnectToPoint, ...relativeWallInfos.mainEndOnWallInternal];
  const startMovable = startPointConnected.length === 0;
  const endMovable = endPointConnected.length === 0;
  const editable = startMovable || endMovable;

  // 墙体自身标尺处理
  if (wallData.baseData.center) {
    const { sagittaRuler, chordRuler } = getArcWallRulerInfos(wallData.baseData as Required<typeof wallData.baseData>);

    rulerInfos.push(
      { wallId: mainWallId, editable: true, arcMidMovable: true, max: MAX_SAGITTA, ...sagittaRuler },
      { wallId: mainWallId, editable, startMovable, endMovable, max: MAX_CHORD_LENGTH, ...chordRuler }
    );
  } else {
    const { startShape, endShape } = wallData.drawWallInfo;
    const leftLineSeg = new LineSeg2d(startShape[0], endShape[0]);

    rulerInfos.push({
      id: 'self',
      start: leftLineSeg.start,
      end: leftLineSeg.end,
      editable,
      wallId: mainWallId,
      startMovable,
      endMovable
    });
  }

  // 查找相连边界
  globalStores.connectedWallOutlines.forEach(({ edges, outest }, index) => {
    if (outest) return;
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];

      if (edge.wallId !== mainWallId || !isMainSideEdge(edge)) {
        continue;
      }

      const prevEdge = edges[(i - 1 + edges.length) % edges.length];
      const nextEdge = edges[(i + 1) % edges.length];

      // 圆弧两端连接同一面墙（穹顶）时，环上只有这两条边，前后邻边是同一条。
      // 该邻边与弧自身的弦长标尺方向一致、数值只差墙厚，属于重复标注，不再生成；
      // 也不能落到下面的前后邻边分支，否则同一条边会被当作前、后邻边各生成一次。
      if (prevEdge === nextEdge) {
        break;
      }

      if (!nextEdge.center && isMainSideEdge(nextEdge) && !isParallel(wallData, nextEdge)) {
        rulerInfos.push({
          id: `${index}-${i}-n`,
          start: nextEdge.start,
          end: nextEdge.end,
          editable: true,
          wallId: nextEdge.wallId,
          startMovable: true
        });
      }

      if (!prevEdge.center && isMainSideEdge(prevEdge) && !isParallel(wallData, prevEdge)) {
        rulerInfos.push({
          id: `${index}-${i}-p`,
          start: prevEdge.start,
          end: prevEdge.end,
          editable: true,
          wallId: prevEdge.wallId,
          endMovable: true
        });
      }
    }
  });

  return rulerInfos;
}

function isMainSideEdge(edge: IWallEdge) {
  return edge.side === EFaceSide.MainLeftSide || edge.side === EFaceSide.MainRightSide;
}

function isParallel(wall: WallData, edge: IWallEdge) {
  return new LineSeg2d(wall.baseData.start, wall.baseData.end).isParallel(new LineSeg2d(edge.start, edge.end));
}
